<template>
  <div class="">
    <b-overlay :show="loading">

      <div class="ibox-title dashboard_title company-form-box-title col-12">
        <h4 class="clearfix">
          <span class="float-left">Compensatory Off Adjustment
            <span class="title-sub-text">
              Assign a compensatory off date to employees who worked on a scheduled off day.
            </span>
          </span>
          <span class="float-right"></span>
        </h4>
      </div>
      <div class="row mt-4 px-3">
        <div class="col-12">

          <div class="row" id="assignment">
            <div class="col-12">

              <b-alert show v-for="error in errors" :key="error" variant="danger">{{ error }}</b-alert>

              <b-form @submit.stop.prevent="onSubmit">
                <div class="row">
                  <div class="col-6">
                    <b-form-group id="from_date-group" label="Compensatory Off Day Date" label-for="from_date" class="required-field mb-3">
                      <b-input-group class="mb-0">
                        <b-form-input @input="loadShiftsData(fdata.from_date)" id="from_date" name="from_date"
                          v-model="fdata.from_date" v-validate="{ required: true, date_format: 'yyyy-mm-dd' }" type="text"
                          placeholder="YYYY-MM-DD" autocomplete="off" :state="validateState('from_date')"
                          aria-describedby="from_date-feedback" data-vv-as="Compensatory Off Day Date" />
                        <b-input-group-append class="mb-0">
                          <b-form-datepicker @input="loadShiftsData(fdata.from_date)" class="mb-0" id="day_one" button-only
                            right locale="en-US" v-model="fdata.from_date" />
                        </b-input-group-append>
                        <b-form-invalid-feedback id="from_date-feedback">
                          {{ veeErrors.first('from_date') }}
                        </b-form-invalid-feedback>
                      </b-input-group>
                    </b-form-group>
                  </div>
                  <div class="col-6">
                    <b-form-group id="to_date-group" label="Original Off-Day Date" label-for="to_date" class="required-field mb-3">
                      <b-input-group class="mb-0">
                        <b-form-input id="to_date" name="to_date" v-model="fdata.to_date"
                          v-validate="{ required: true, date_format: 'yyyy-mm-dd' }" type="text" placeholder="YYYY-MM-DD"
                          autocomplete="off" :state="validateState('to_date')" aria-describedby="to_date-feedback"
                          data-vv-as="Original Off-Day Date" />
                        <b-input-group-append class="mb-0">
                          <b-form-datepicker class="mb-0" id="day_two" button-only right locale="en-US"
                            v-model="fdata.to_date" />
                        </b-input-group-append>
                        <b-form-invalid-feedback id="to_date-feedback">
                          {{ veeErrors.first('to_date') }}
                        </b-form-invalid-feedback>
                      </b-input-group>
                    </b-form-group>
                  </div>
                </div>

                <b-form-group id="reason-group" label="Reason" label-for="reason" class="mb-3">
                  <b-form-textarea id="reason" name="reason" rows="3" v-model="fdata.reason"
                    placeholder="Enter reason for assignment" autocomplete="off" ></b-form-textarea>
                </b-form-group>

                <div class="text-left">
                  <b-button type="button" variant="primary" :disabled="employee_ids.length == 0"
                  @click="openAssignModal">
                    Assign Compensatory Off <template v-if="employee_ids.length > 0">({{ employee_ids.length }}
                      Selected)</template>
                  </b-button>
                </div>
              </b-form>
              
              <div class="">
                <common-list :checkbox="1" :is_action="0" :appended_column="appended_column" :key="list_key"
                  :company_options="company_options" :employees="custom_list" :all_employee="'no'"
                  :default_column="default_column" :primary_key="'employee_id'"
                  v-on:childToParent="listReturn"></common-list>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <b-modal id="compensatory-assign-modal" title="Compensatory Off Assignment" size="lg" hide-footer>
      <b-overlay :show="loading" rounded="sm">
        <div class="mb-4">
          <div class="alert alert-info py-2 px-3 mb-4">
            <i class="fa fa-info-circle mr-1"></i> Please review the assignment details before confirming:
          </div>

          <div class="row mb-4">
            <div class="col-md-4">
              <div class="p-3 ibox text-center h-100">
                <span class="text-muted d-block text-uppercase font-weight-bold mb-1">Compensatory Off Date</span>
                <span class="h5 mb-0 text-success font-weight-bold">{{ fdata.from_date }}</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="p-3 ibox text-center h-100">
                <span class="text-muted d-block text-uppercase font-weight-bold mb-1">Duty Date</span>
                <span class="h5 mb-0 text-primary font-weight-bold">{{ fdata.to_date }}</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="p-3 ibox text-center h-100">
                <span class="text-muted d-block text-uppercase font-weight-bold mb-1">Reason</span>
                <span class="text-truncate d-block mb-0 font-weight-bold" :title="fdata.reason || 'N/A'">{{ fdata.reason || 'N/A' }}</span>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="font-weight-bold mb-0">Selected Employees ({{ selectedEmployeesSummary.length }})</label>
          </div>

          <div class="table-responsive rounded">
            <table class="table table-hover table-striped table-sticky">
              <thead class="table-head">
                <tr>
                  <th>Code</th>
                  <th>Employee Name</th>
                  <th>Job Position</th>
                  <th>Department</th>
                  <th>Current Shift</th>
                  <th>Off Day Assigned</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="emp in selectedEmployeesSummary" :key="emp.employee_id">
                  <td class="align-middle">{{ emp.employee_code }}</td>
                  <td class="align-middle">{{ emp.name }}</td>
                  <td class="align-middle">{{ emp.jobposition || 'N/A' }}</td>
                  <td class="align-middle">{{ emp.department || 'N/A' }}</td>
                  <td class="align-middle">
                    <span v-if="emp.shift">
                      {{ emp.shift }}
                    </span>
                    <span v-else class="">
                      No Shift
                    </span>
                  </td>
                  <td class="align-middle">{{ fdata.from_date }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="text-center pt-3">
          <b-button @click="$bvModal.hide('compensatory-assign-modal')" type="button" variant="secondary" class="mr-2 px-4">
            Cancel
          </b-button>
          <b-button type="button" variant="primary" :disabled="loading" @click="confirmAndSubmit" class="px-4">
            Confirm & Assign
          </b-button>
        </div>
      </b-overlay>
    </b-modal>

  </div>
</template>

<script>

import moment from "moment";

export default {
  props: ['roster'],
  data() {
    return {
      assignments: [],
      loading: false,
      primary_key: 'employee_id',
      company_options: [],
      y: 2025,
      m: 8,
      errors: [],
      checkbox: 0,
      list_key: 0,
      is_action: 0,
      custom_list: [],
      depertments: [],
      default_column: ['name', 'employee_code', 'jobposition', 'branch', 'department'],
      fdata: {
        from_date: moment().format('YYYY-MM-DD'),
        to_date: '',
        overlap: 0,
        reason: '',
      },
      employee_ids: [],
      localRoster: null,
      shifts: [],
      branchOptions: [],
    }
  },

  computed: {
    rosterValue() {
      return this.localRoster !== null ? this.localRoster : this.roster;
    },
    appended_column() {
      return this.rosterValue == 1 ? [{ value: "shift", text: "Assigned Shift", type: "text" }] : [];
    },
    selectedEmployeesSummary() {
      return this.custom_list.filter(emp => this.employee_ids.includes(emp.employee_id));
    }
  },

  methods: {
    moment,
    validateState(ref) {
      if (
        this.veeFields[ref] &&
        (this.veeFields[ref].dirty || this.veeFields[ref].validated)
      ) {
        return !this.veeErrors.has(ref);
      }
      return null;
    },

    avatar(e) {
      return `/avatar/${e.company_id}/${e.pic}`;
    },

    loadShiftsData(date) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return false;
      }
      this.error = null;

      const formData = new FormData();
      formData.append('date', date);
      formData.append('download', 0);

      axios.post(this.$api_url + '/leave-attendance/daily', formData).then(response => {
        this.assignments = response.data.reduce((acc, item) => {
          acc[item.employee_id] = item.shift_name;
          return acc;
        }, {});

        for (let [k, v] of Object.entries(this.custom_list)) {
          this.$set(this.custom_list[k], 'shift', this.assignments[this.custom_list[k]['employee_id']] ?? '');
        }
        this.list_key++;
        this.errors = [];
      }).catch(e => this.serverError(e)).finally(() => { this.loading = false; });

    },

    listReturn(data) {
      this.employee_ids = data.selected;
    },

    getEmployees() {
      let url = this.$api_url + '/leave-attendance/employees/list';
      let params = {
        general: 1,
        fields: 'shift'
      };
      this.list_key++;
      this.loading = true;
      axios.get(url, { params: params }).then(response => {
        this.list_key++;
        this.custom_list = response.data.employee_data;
        this.company_options = response.data.options;
        this.loading = false;
        // Fetch shift data after employee list has loaded
        this.loadShiftsData(this.fdata.from_date);
      });
    },

    parseYMD: function (dateStr) {
      if (!dateStr || typeof dateStr !== 'string') {
        return new Date();
      }
      const [y, m, d] = dateStr.split('-').map(Number);
      return new Date(y, m - 1, d);
    },

    openAssignModal() {
      this.$validator.validateAll().then(valid => {
        if (valid) {
          this.$bvModal.show('compensatory-assign-modal');
        }
      });
    },

    onSubmit() {
      this.$validator.validateAll().then(valid => {
        if (!valid) {
          return;
        }

        if (this.employee_ids.length == 0) {
          this.$bvToast.toast("Please select employee(s) first", {
            variant: 'danger',
            solid: true,
            title: 'Failed',
          });
          return;
        }
      });
    },

    confirmAndSubmit() {
      this.loading = true;

      const formData = new FormData();
      formData.append('employee_ids', JSON.stringify(this.employee_ids));
      Object.entries(this.fdata).forEach(([key, val]) => {
        formData.append(key, val);
      });
      formData.append('from_date', this.fdata.from_date);
      formData.append('to_date', this.fdata.to_date);

      axios.post(this.$api_url + '/leave-attendance/compensatory-assign', formData).then(response => {
        if (response.data.errors) {
          this.errors = response.data.errors;
          this.loading = false;
          this.$bvModal.hide('compensatory-assign-modal');
        } else {
          this.list_key++;
          this.employee_ids = [];
          this.getEmployees();
          this.loading = false;
          this.$bvToast.toast(response.data.message, {
            variant: 'success',
            title: 'Success',
            solid: true,
          });
          this.$bvModal.hide('compensatory-assign-modal');
          this.$nextTick().then(_ => {
            this.resetForm();
            this.$validator.reset();
            this.errors = [];
          });
        }
      }).catch(e => {
        this.loading = false;
        this.serverError(e);
      });
    },

    resetForm() {
      this.fdata = {
        from_date: moment().format('YYYY-MM-DD'),
        to_date: '',
        overlap: 0,
        reason: '',
      };
    },
    getCompanyLeaveSettings: function () {
      if (!this.checkNet()) return false;
      axios.get(this.$api_url + '/leave-attendance/company/leave/settings').then(function (response) {

        if (response.data.length != 0) {
          this.localRoster = response.data.roster
        }

      }.bind(this));

    },

  },
  created() {
    this.$validator.reset();
    this.getEmployees();
    this.getCompanyLeaveSettings();
  },
};
</script>